<template>
  <BaseButton
    v-bind="{ ...$attrs, ...computedProps }"
    :variant="resolvedVariant"
  >
    <template v-if="$slots.default">
      <slot />
    </template>
  </BaseButton>
</template>

<script setup lang="ts">
/**
 * SuccessButton — approve / accept / confirm / publish (positive intent).
 *
 * Default paint is a solid green filled button so the affirmative action
 * reads as deliberate. Set `tonal` for the softer green-on-blush form (use
 * in moderation queues / tables where a wall of green would shout).
 *
 * Use instead of a raw `<q-btn color="positive" />`.
 */
import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import BaseButton from './BaseButton.vue';
import type { ButtonSize } from './BaseButton.vue';

interface Props {
  to?: RouteLocationRaw;
  href?: string;
  external?: boolean;
  type?: 'button' | 'submit' | 'reset';
  label?: string;
  icon?: string;
  iconRight?: string;
  tooltip?: string;
  disabledTooltip?: string;
  loading?: boolean;
  disable?: boolean;
  size?: ButtonSize;
  fullWidth?: boolean;
  pill?: boolean;
  /** Soft tinted green instead of solid green filled. */
  tonal?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  type: 'button',
  external: false,
  loading: false,
  disable: false,
  size: 'md',
  fullWidth: false,
  pill: false,
  tonal: false,
});

const resolvedVariant = computed<'success' | 'success-tonal'>(() =>
  props.tonal ? 'success-tonal' : 'success',
);

const computedProps = computed(() => {
  const result: Record<string, unknown> = {
    type: props.type,
    external: props.external,
    loading: props.loading,
    disable: props.disable,
    size: props.size,
    fullWidth: props.fullWidth,
    pill: props.pill,
  };
  if (props.to !== undefined) result.to = props.to;
  if (props.href !== undefined) result.href = props.href;
  if (props.label !== undefined) result.label = props.label;
  if (props.icon !== undefined) result.icon = props.icon;
  if (props.iconRight !== undefined) result.iconRight = props.iconRight;
  if (props.tooltip !== undefined) result.tooltip = props.tooltip;
  if (props.disabledTooltip !== undefined) result.disabledTooltip = props.disabledTooltip;
  return result;
});

defineOptions({
  name: 'SuccessButton',
  inheritAttrs: false,
});
</script>
